import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { analyticsService } from '@common/analytics';
import Input, { SIGNUP_EMAIL, SIGNUP_PASSWORD, SIGNUP_RETYPE_EMAIL } from './input';
import classes from '../classes.module.scss';
import { passwordIsValid, generalCreditFormatForUSD } from '../../../config/util';
import UsePreviousValue, { emailIsValid } from '../../../../common/config/utils';
import Const from '../../../config/const';
import GDPRConsernPopup from '../gdpr_consern_popup';
import { GDPRConsernPopupThemes } from '../../../config/themes';
import PhoneNumberCta from './phone_number_cta';
import { useNotification } from '../../firebase_messaging_provider';

function SignUp({
  email,
  password,
  confirmEmail,
  switcherChecked,
  loading,
  error,
  t,
  social,
  onRegister,
  onFieldChanged,
  rememberMe,
  changeScreen,
  theme = Const.authScreens.signUp,
  checkInputErrors,
  clearError,
  shouldShowGDPRConsent,
  userMatchUpBonusInfo,
  showLoginByPhone
}) {
  const [userMatchUpBonusInfosState] = useState(userMatchUpBonusInfo);
  const { requestPermission } = useNotification();

  useEffect(() => {
    analyticsService.trackPageVisit('sign up with email');
  }, []);

  const allFieldsAreValid = emailIsValid(email)
    && passwordIsValid(password, true) && email === confirmEmail;

  const showPrivacyModal = () => {
    checkInputErrors();
    if (allFieldsAreValid) {
      shouldShowGDPRConsent({ [GDPRConsernPopupThemes.regular]: true });
    }
  };

  const onRegisterClicked = () => {
    checkInputErrors();
    if (allFieldsAreValid) {
      requestPermission(true);
      onRegister();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (!switcherChecked) {
        showPrivacyModal();
      } else {
        onRegisterClicked();
      }
    }
  };

  const renderPrivacyText = () => (
    <div className = { classes.confirmPpTofTextContainer }>
      { t('app_ppTof.you_must_be_at_least_18_years') }
    </div>
  );

  const renderaAffiliatePromoHeader = () => {
    if (theme !== Const.authScreens.signUpAffiliatePromo) return null;
    const prevUserMatchUpBonusInfo = <UsePreviousValue value={ userMatchUpBonusInfosState } />;
    const prevUserMatchUpBonusInfoValue = prevUserMatchUpBonusInfo.props.value;
    return (
      <div className={ classes.affiliatePromoHeaderContainer }>
        <div className={ classes.affiliatePromoHeaderTitle }>
          { t('affitiate_promo_sign_up.claim_your_special_matching_bonus') }
        </div>
        <div className={ classes.affiliatePromoHeaderBottomText }>
          { t('affitiate_promo_sign_up.buy_credit', {
            amount: generalCreditFormatForUSD(userMatchUpBonusInfo?.requiredPurchasesAmount || prevUserMatchUpBonusInfoValue?.requiredPurchasesAmount),
            extra_amount: generalCreditFormatForUSD(userMatchUpBonusInfo?.bonus || prevUserMatchUpBonusInfoValue?.bonus)
          })}
        </div>
      </div>
    );
  };

  const navigateToSignIn = () => {
    changeScreen(Const.authScreens.signIn);
    clearError();
  };

  const renderAlreadyHaveAnAccountButton = () => {
    if (theme === Const.authScreens.signUpAffiliatePromo) return null;
    return (
      <div className={ classes.navigationButtonContainer }>
        <button className={ classes.navigationButton } type="button" onClick={ navigateToSignIn }>
          <b>{t('already_have')}</b>
        </button>
      </div>
    );
  };

  const renderSignUpAffiliatePromoLabel = (text) => (
    theme === Const.authScreens.signUpAffiliatePromo
      ? (
        <div className={ classes.signUpAffiliatePromoLabel }>
          { text }
        </div>
      ) : null
  );

  return (
    <div data-testid="sign-up-container" style={ { maxWidth: '334px' } }>
      { renderaAffiliatePromoHeader() }
      {social}
      <div className={ classes.connectTitle }>
        { theme === Const.authScreens.signUpAffiliatePromo
          ? (
            <div className={ classes.signUpAffiliatePromoLabel }>
              { t('affitiate_promo_sign_up.or') }
            </div>
          ) : (
            <div style={ { paddingTop: '24px' } }>
              { t('or_create_account') }
            </div>
          )}
      </div>
      <div>
        { renderSignUpAffiliatePromoLabel(t('email')) }
        <Input
          inputType={ SIGNUP_EMAIL }
          onKeyDown={ handleKeyPress }
          onChange={ onFieldChanged }
          value={ email }
          id="email"
          disabled={ loading }
          error={ error && error.email && error.email[0] }
          theme={ theme }
        />
      </div>
      <div>
        { renderSignUpAffiliatePromoLabel(t('retype_email')) }
        <Input
          inputType={ SIGNUP_RETYPE_EMAIL }
          onKeyDown={ handleKeyPress }
          onChange={ onFieldChanged }
          value={ confirmEmail }
          id="confirmEmail"
          disabled={ loading }
          error={ error && error.confirmEmail && error.confirmEmail[0] }
          theme={ theme }
        />
      </div>
      <div>
        { renderSignUpAffiliatePromoLabel(t('password')) }
        <Input
          inputType={ SIGNUP_PASSWORD }
          onKeyDown={ handleKeyPress }
          onChange={ onFieldChanged }
          value={ password }
          id="password"
          disabled={ loading }
          error={ error && error.password && error.password[0] }
          theme={ theme }
        />
      </div>
      {rememberMe}
      <div>
        <button
          data-testid="sign-up-submit-button"
          className={ classNames(
            classes.actionButton,
            theme === Const.authScreens.signUpAffiliatePromo
              ? classes.signUpAffiliatePromoactionButton : null
          ) }
          type="button"
          onClick={ !switcherChecked ? showPrivacyModal : onRegisterClicked }
        >
          <b>{theme === Const.authScreens.signUpAffiliatePromo ? t('affitiate_promo_sign_up.sign_up') : t('create_account')}</b>
        </button>
      </div>
      { renderPrivacyText() }
      {showLoginByPhone && (
        <PhoneNumberCta
          changeScreen={ changeScreen }
          t={ t }
          loading={ loading }
        />
      )}
      { renderAlreadyHaveAnAccountButton() }
      <GDPRConsernPopup onRegisterClicked={ onRegisterClicked } theme={ GDPRConsernPopupThemes.regular } />
    </div>
  );
}

export default SignUp;
